import { Component, output, ChangeDetectionStrategy } from '@angular/core';
import {
  WorkflowNodeType,
  WorkflowNodeRuleType,
  WorkflowNodeActionType,
  nodeTypeIconFor,
  nodeTypeLabelFor
} from '@formrails/workflow-designer/data-access';

/** A node creation request emitted by the palette (position is decided by the canvas/feature). */
export interface NodePaletteSelection {
  type: WorkflowNodeType;
  ruleType?: WorkflowNodeRuleType;
  actionType?: WorkflowNodeActionType;
}

interface PaletteItem {
  label: string;
  icon: string;
  selection: NodePaletteSelection;
}

interface PaletteGroup {
  label: string;
  items: PaletteItem[];
}

/**
 * Entries take their label and icon from the shared mappings rather than naming
 * either, so a palette button and the properties panel for the node it adds
 * cannot drift apart.
 */
function paletteItem(selection: NodePaletteSelection): PaletteItem {
  return { label: nodeTypeLabelFor(selection), icon: nodeTypeIconFor(selection), selection };
}

/**
 * Palette of workflow node types, grouped by WorkflowNodeType with the Action
 * and Rule sub-types expanded into their own selectable items.
 */
@Component({
  selector: 'app-workflow-node-palette',
  standalone: true,
  imports: [],
  templateUrl: './workflow-node-palette.component.html',
  styleUrl: './workflow-node-palette.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowNodePaletteComponent {
  /** Emitted when the user picks a node to add. */
  readonly addNode = output<NodePaletteSelection>();

  readonly groups: PaletteGroup[] = [
    {
      label: 'Flow',
      items: [
        paletteItem({ type: WorkflowNodeType.Trigger }),
        paletteItem({ type: WorkflowNodeType.Terminator })
      ]
    },
    {
      label: 'Rules',
      items: [
        paletteItem({
          type: WorkflowNodeType.Rule,
          ruleType: WorkflowNodeRuleType.Condition
        }),
        paletteItem({
          type: WorkflowNodeType.Rule,
          ruleType: WorkflowNodeRuleType.WaitForTrigger
        }),
        paletteItem({
          type: WorkflowNodeType.Rule,
          ruleType: WorkflowNodeRuleType.TimeDelay
        })
      ]
    },
    {
      label: 'Actions',
      items: [
        paletteItem({
          type: WorkflowNodeType.Action,
          actionType: WorkflowNodeActionType.SendEmail
        }),
        paletteItem({
          type: WorkflowNodeType.Action,
          actionType: WorkflowNodeActionType.Webhook
        })
      ]
    }
  ];

  onAdd(item: PaletteItem): void {
    this.addNode.emit(item.selection);
  }
}
